<template>
  <div>
    <popup-hint hint="processes">
      <template v-slot:breadcrumbs>
        <breadcrumbs :back="[{text:'Distilling',to:{name:'ProductionDistilling'}}]" home="Processes"/>
      </template>
      <template v-slot:top>
        Processes are templates for distilling.
        They allow you to specify a consistent set of inputs and outputs
        to use for a distillation.
      </template>
      <template v-slot:bottom>
          A process will save you time,
          ensures consistency, and allows you to gather statistics
          over time to look for trends and issues.
      </template>
      <template v-slot:actions="{close}">
        <v-btn @click="close()" :to="{name:'ProductionDistillingProcessesEditNew'}" color="primary" class="ml-4" small>Create</v-btn>
      </template>
    </popup-hint>

    <purtrak-table name="processes" objectType="processes" :defaultColumns="defaultColumns" :headers="headers" ref="table" show-select show-expand
                   listEndPoint="processes2" v-model="selected"
                   fields="id,name,active,duration"
                   :listFilters="listFilters"
                   :item-class="process => (process.active ? '' : 'grey--text')"
                   @dblclick="process => $router.push({name:'ProductionDistilling',params:{processId:process.id}})"
                   @itemExpanded="({item, value}) => expandProcess(item, value)"
                   title="Processes"
                   :print-info="{pageSize:'portrait'}"
                   download-endpoint="processesExportCSV">
      <template v-slot:filter>
        <filter-menu include-reset @reset="filters.status='1';filters.search=''">
          <v-list-item>
            <filter-search label="Name" v-model="filters.search"/>
          </v-list-item>
          <v-list-item>
            <v-autocomplete label="Status" dense outlined :items="[{value:'2',text:'All'},{divider:true},{value:'1',text:'Active'},{value:'0',text:'Inactive'}]" v-model="filters.status"/>
          </v-list-item>
        </filter-menu>
        <template v-if="!$vuetify.breakpoint.mobile">
          <v-chip v-show="filters.search" close small class="ml-1" @click:close="filters.search=''">Processes: {{filters.search}}</v-chip>
          <v-chip v-show="filters.status!='1'" close small class="ml-1" @click:close="filters.status='1'">{{filters.status=='0'?'Inactive':'All'}}</v-chip>
        </template>
      </template>
      <template v-slot:menu>
        <template v-if="!readOnly">
          <v-list-item :to="{name:'ProductionDistillingProcessesEditNew'}"><v-list-item-title>Create process</v-list-item-title></v-list-item>
          <v-divider/>
        </template>
        <v-list-item :to="{name:'ReportsView',params:{back:[{text:'Distilling',to:{name:'ProductionDistilling'}},{text:'Processes',to:{name:'ProductionDistillingProcesses'}}]},query:{to:'reports/production/ReportsProcessPerformance'}}"><v-list-item-title>Process performance report</v-list-item-title></v-list-item>
      </template>

      <template v-slot:header.action v-if="!readOnly">
        <v-btn fab color="primary" small :to="{name:'ProductionDistillingProcessesEditNew'}"><v-icon>mdi-plus</v-icon></v-btn>
      </template>
      <template v-slot:item.name="{item,printing}">
        <template v-if="printing">{{item.name}}</template>
        <template v-else>
          <v-hover v-if="!readOnly" v-slot="{hover}">
            <a @click.stop="$router.push({name:'ProductionDistillingProcessesEdit',params:{processId:item.id}})"
               :class="{'text-decoration-underline':hover}">{{item.name}}</a>
          </v-hover>
          <template v-else>{{item.name}}</template>
          <div v-if="!item.active" class="grey--text text-caption">Inactive</div>
        </template>
      </template>
      <template v-slot:item.create="{item}">
        <v-btn v-if="item.active" rounded color="success" small :to="{name:'ProductionDistillingEditNew',params:{processId:item.id}}">Distill</v-btn>
      </template>
      <template v-slot:item.action="{item}">
        <v-menu :nudge-width="150" offset-y>
          <template v-slot:activator="{ on }">
            <v-btn small v-on="on" v-if="$vuetify.breakpoint.mobile" outlined class="ml-4">Process operations</v-btn>
            <v-btn small icon v-on="on" v-else><v-icon small>mdi-dots-vertical</v-icon></v-btn>
          </template>
          <v-list dense>
            <template v-if="!readOnly">
              <v-list-item v-if="item.active" exact :to="{name:'ProductionDistillingEditNew',params:{processId:item.id}}"><v-list-item-title>Distill</v-list-item-title></v-list-item>
              <v-list-item @click="printDistillation(item.id,{},true)"><v-list-item-title>Print</v-list-item-title></v-list-item>
              <v-divider/>
            </template>
            <v-list-item :to="{name:'ProductionDistilling',query:{processId:item.id}}"><v-list-item-title>Transactions</v-list-item-title></v-list-item>

            <v-menu :nudge-left="170" v-if="!$vuetify.breakpoint.mobile">
              <template v-slot:activator="{on:reports}">
                <v-list-item v-on="reports">
                  <v-list-item-title>Reports</v-list-item-title>
                  <v-list-item-action><v-icon>mdi-chevron-right</v-icon></v-list-item-action>
                </v-list-item>
              </template>
              <v-list dense>
                <v-list-item :to="{name:'ReportsView',params:{back:[{text:'Distilling',to:{name:'ProductionDistilling'}},{text:'Processes',to:{name:'ProductionDistillingProcesses'}}]},query:{to:'reports/production/ReportsDistillationActivity',processId:item.id}}"><v-list-item-title>Distilling activity</v-list-item-title></v-list-item>
                <v-list-item :to="{name:'ReportsView',params:{back:[{text:'Distilling',to:{name:'ProductionDistilling'}},{text:'Processes',to:{name:'ProductionDistillingProcesses'}}]},query:{to:'reports/production/ReportsProcessPerformance',processId:item.id}}"><v-list-item-title>Process performance</v-list-item-title></v-list-item>
              </v-list>
            </v-menu>

            <v-list-item v-if="company.allowAudit && company.permissions.includes('audit')" :to="{name:'Audit',query:{entityId:item.id}}"><v-list-item-title>Audit history</v-list-item-title></v-list-item>
            <template v-if="!readOnly">
              <v-divider/>
              <v-list-item :to="{name:'ProductionDistillingProcessesEdit',params:{processId:item.id}}"><v-list-item-title>Edit process</v-list-item-title></v-list-item>
              <v-list-item :to="{name:'ProductionDistillingProcessesEdit',params:{processId:item.id,copy:true}}"><v-list-item-title>Copy process</v-list-item-title></v-list-item>
              <v-list-item @click="process=item;showDelete=true"><v-list-item-title>Delete process</v-list-item-title></v-list-item>
            </template>
          </v-list>
        </v-menu>
      </template>
      <template v-slot:item.duration="{value}">{{value | duration}}</template>
      <template v-slot:expanded-item="{ headers,item }">
        <td :colspan="headers.length" >
          <v-card class="my-4" :class="expansionPanelClass">
            <v-container>
              <v-row dense>
                <v-col cols="6">
                  <v-simple-table dense class="pl-4 text-caption" :class="expansionPanelClass" style="width:100%">
                    <thead>
                      <tr>
                        <th :class="expansionPanelClass" width="60%">Input</th>
                        <th class="text-right" :class="expansionPanelClass" width="60%">Quantity</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="input of item.inputs" :key="input.id">
                        <td class="text-caption">{{input.itemName}}</td>
                        <td class="text-caption text-right">{{input.quantity | number(input.precision)}} {{input.itemUnits}}</td>
                      </tr>
                    </tbody>
                  </v-simple-table>
                </v-col>
                <v-col cols="1"/>
                <v-col cols="5">
                  <v-simple-table dense class="pl-4 text-caption" :class="expansionPanelClass" style="width:100%">
                    <thead>
                      <tr>
                        <th :class="expansionPanelClass" width="100%">Output</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="output of item.outputs" :key="output.spiritId">
                        <td class="text-caption">{{output.spiritName}}</td>
                      </tr>
                    </tbody>
                  </v-simple-table>
                </v-col>
              </v-row>
            </v-container>
          </v-card>
        </td>
      </template>
    </purtrak-table>

    <delete-object v-model="showDelete" :name="process.name" @delete="performDeleteProcess"/>
  </div>
</template>

<script>
import productionDistillingPrintDistillation from '@/views/production/distilling/productionDistillingPrintDistillation'

export default {
  name: 'ProductionDistillingProcesses',
  mixins: [productionDistillingPrintDistillation],
  data() {
    return {
      filters: {
        status: '1',
        search: '',
      },
      preferences: {},
      process: {},
      showDelete: false,
      defaultColumns: [{ value: 'name', visible:true},
                       { value: 'duration', visible:true},
                       { value: 'create', visible:true}],
      headers: [{ text: 'PROCESS', value: 'name', width:'60%',sortable:true},
                { text: 'AVG DURATION', value: 'duration', width:'30%',sortable:true, omit:() => !this.preferences.distillDuration},
                { text: 'CREATE', value: 'create', width:'15%',sortable:false,align:'center',print:false}],
    }
  },
  computed: {
    company() {
      return this.$store.getters.company;
    },
    readOnly() {
      return !this.company.permissions.includes('production.write');
    },
    listFilters() {
      const filters = [];
      if (this.filters.status !== '2')
        filters.push({field:'active', comparator:'eq', value:this.filters.status == '1' ? 'true' : 'false'});
      if (this.filters.search)
        filters.push({field:'name', comparator:'contains', value:this.filters.search});
      return filters;
    }
  },
  async created() {
    const results = await this.$apiCall(`query ProductionDistillingProcesses {
                                              distillingPreferences {
                                                distillDuration
                                              }
                                            }`);
    if (!results.error)
      this.preferences = results.data.distillingPreferences;
    this.loading = false;
  },
  methods: {
    async expandProcess(item, value) {
      if (value && !item.inputs) {
        this.loading = true;
        const results = await this.$apiCall(`query ProductionDistillingProcesses($id:ID!) {
                                                  process(id:$id) {
                                                    inputs{id,itemName,itemUnits,precision,quantity},
                                                    outputs{spiritId,spiritName}
                                                  }
                                                }`,
                                            {id:item.id});
        this.$set(item, 'inputs', results.data.process.inputs);
        this.$set(item, 'outputs', results.data.process.outputs);
        this.loading = false;
      }
    },
    async performDeleteProcess() {
      this.loading = true;
      const results = await this.$apiCall(`mutation ProductionDistillingProcesses($id: ID!) {
                                             objectDelete(id:$id)
                                            }`,
                                          {id:this.process.id});
      this.loading = false;
      if (!results.error) {
        this.showDelete = false;
        this.$store.commit('setError', {type:'success',message:`Deleted process ${this.process.name}`});
        this.$refs.table?.buildList();
      }
    }
  }
}
</script>